import type { OpportunityDetailFieldKey } from '../types';

export const fieldLabelMap: Record<string, string> = {
  currentProcess: 'Current Process',
  expectedImpact: 'Expected Impact',
  specificIdeasOfImprovement: 'Specific Idea(s) for Improvement',
  keyPerformanceIndicators: 'Key Performance Indicators',
  // Solution fields
  opportunity_statement_solutionxplr: 'Opportunity Statement',
  current_process_solutionxplr: 'Current Process (Solution)',
  proposed_enhancements_solutionxplr: 'Proposed Enhancements',
  estimated_impact_solutionxplr: 'Estimated Impact (Solution)',
  opportunityStatementSolutionXplr: 'Opportunity Statement',
  currentProcessSolutionXplr: 'Current Process (Solution)',
  proposedEnhancementsSolutionXplr: 'Proposed Enhancements',
  estimatedImpactSolutionXplr: 'Estimated Impact (Solution)',
  current_process: 'Current Process',
  expected_impact: 'Expected Impact',
  ideas_of_improvement: 'Specific Idea(s) for Improvement',
  key_performance_indicator: 'Key Performance Indicators',
};

export const fieldAPIKeyMap: Record<string, string> = {
  currentProcess: 'current_process',
  expectedImpact: 'expected_impact',
  specificIdeasOfImprovement: 'ideas_of_improvement',
  keyPerformanceIndicators: 'key_performance_indicator',
  opportunityStatementSolutionXplr: 'opportunity_statement_solutionxplr',
  currentProcessSolutionXplr: 'current_process_solutionxplr',
  proposedEnhancementsSolutionXplr: 'proposed_enhancements_solutionxplr',
  estimatedImpactSolutionXplr: 'estimated_impact_solutionxplr',
  'Current Process': 'current_process',
  'Expected Impact': 'expected_impact',
  'Specific Idea of Improvement': 'ideas_of_improvement',
  'Specific Ideas of Improvement': 'ideas_of_improvement',
  'Specific Idea(s) for Improvement': 'ideas_of_improvement',
  'Key Performance Indicators': 'key_performance_indicator',

  current_process: 'current_process',
  expected_impact: 'expected_impact',
  ideas_of_improvement: 'ideas_of_improvement',
  key_performance_indicator: 'key_performance_indicator',
  // Solution fields (identity mapping - already in API format)
  opportunity_statement_solutionxplr: 'opportunity_statement_solutionxplr',
  current_process_solutionxplr: 'current_process_solutionxplr',
  proposed_enhancements_solutionxplr: 'proposed_enhancements_solutionxplr',
  estimated_impact_solutionxplr: 'estimated_impact_solutionxplr',
  'Opportunity Statement': 'opportunity_statement_solutionxplr',
  'Current Process (Solution)': 'current_process_solutionxplr',
  'Proposed Enhancements': 'proposed_enhancements_solutionxplr',
  'Estimated Impact (Solution)': 'estimated_impact_solutionxplr',
};

export const fieldConversationTypeMap: Record<string, string> = {
  currentProcess: 'ideation_xplr',
  expectedImpact: 'ideation_xplr',
  specificIdeasOfImprovement: 'ideation_xplr',
  keyPerformanceIndicators: 'ideation_xplr',

  opportunityStatementSolutionXplr: 'solution_xplr',
  currentProcessSolutionXplr: 'solution_xplr',
  proposedEnhancementsSolutionXplr: 'solution_xplr',
  estimatedImpactSolutionXplr: 'solution_xplr',
};

export const chatBotFieldMap: Record<string, string> = {
  currentProcess: 'current_process_chat',
  expectedImpact: 'expected_impact_chat',
  specificIdeasOfImprovement: 'specific_ideas_of_improvement_chat',
  keyPerformanceIndicators: 'key_performance_indicators_chat',
  opportunityStatementSolutionXplr: 'opportunity_statement_solutionxplr_chat',
  currentProcessSolutionXplr: 'current_process_solutionxplr_chat',
  proposedEnhancementsSolutionXplr: 'proposed_enhancements_solutionxplr_chat',
  estimatedImpactSolutionXplr: 'estimated_impact_solutionxplr_chat',
};

export const getFieldKeyFromCategory: Record<string, string> = {
  'Current Process': 'current_process',
  'Expected Impact': 'expected_impact',
  KPIs: 'key_performance_indicator',
  'Specific Idea of Improvement': 'ideas_of_improvement',
  'Specific Ideas of Improvement': 'ideas_of_improvement',
  'Specific Idea(s) for Improvement': 'ideas_of_improvement',
};

export const opportunityDetailsFieldKeyMap: Record<
  OpportunityDetailFieldKey,
  OpportunityDetailFieldKey
> = {
  id: 'id',
  currentProcess: 'currentProcess',
  expectedImpact: 'expectedImpact',
  specificIdeasOfImprovement: 'specificIdeasOfImprovement',
  keyPerformanceIndicators: 'keyPerformanceIndicators',
  opportunityStatementSolutionXplr: 'opportunityStatementSolutionXplr',
  currentProcessSolutionXplr: 'currentProcessSolutionXplr',
  proposedEnhancementsSolutionXplr: 'proposedEnhancementsSolutionXplr',
  estimatedImpactSolutionXplr: 'estimatedImpactSolutionXplr',
  domain: 'domain',
  selectedFunction: 'selectedFunction',
  selectedFunctionId: 'selectedFunctionId',
  selectedProcessGroup: 'selectedProcessGroup',
  selectedProcessGroupId: 'selectedProcessGroupId',
  selectedProcess: 'selectedProcess',
  selectedProcessId: 'selectedProcessId',
  technologies: 'technologies',
  uploadedFiles: 'uploadedFiles',
  selectedContexts: 'selectedContexts',
  detailsText: 'detailsText',
  taxonomy: 'taxonomy',
  status: 'status',
  finalSummary: 'finalSummary',
  prefillUseCaseId: 'prefillUseCaseId',
  isPrefillViewMode: 'isPrefillViewMode',
  cumulativeFieldData: 'cumulativeFieldData',
  useDataXplrFields: 'useDataXplrFields',
  recent_imported_chat: 'recent_imported_chat',
  showRegenerateButton: 'showRegenerateButton',
  cardToBeRegeneratedId: 'cardToBeRegeneratedId',
  useCaseStatusOverrides: 'useCaseStatusOverrides',
  proposedEnhancements: 'proposedEnhancements',
  linkedSessions: 'linkedSessions',
  deletedUseCaseId: 'deletedUseCaseId',
  isRegenerateAllowed: 'isRegenerateAllowed',
  isVersionViewMode: 'isVersionViewMode',
  previewVersionData: 'previewVersionData',
  is_restore_blocked: 'is_restore_blocked',
};

export const orchestratorFieldNameToOpportunityFieldKey: Record<string, OpportunityDetailFieldKey> =
  {
    current_process: opportunityDetailsFieldKeyMap.currentProcess,
    expected_impact: opportunityDetailsFieldKeyMap.expectedImpact,
    improvement_ideas: opportunityDetailsFieldKeyMap.specificIdeasOfImprovement,
    relevant_kpis: opportunityDetailsFieldKeyMap.keyPerformanceIndicators,
    enterprise_apps: opportunityDetailsFieldKeyMap.technologies,
  };

export const DOMAIN_OPTIONS = [
  { label: 'Back Office', value: 'backoffice' },
  { label: 'Middle Office', value: 'middleoffice' },
  { label: 'Front Office', value: 'frontoffice' },
] as const;
